import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import WorkshopTodayRevenueBar from "./components/workshop/WorkshopTodayRevenueBar";
import Ikon from "./ny-kundesamhandling/Ikon";
import { Tilbake, Sidetopp, Valgfelt, knappPrimar, knappSekundar } from "./ny-kundesamhandling/Skjema";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;



const fmtDate = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";
};

function renameTjeneste(navn, slug = "") {
  if (
    String(slug || "").toLowerCase() === "garantihenvendelse" ||
    String(navn || "").toLowerCase() === "garantihenvendelse"
  ) {
    return "Reklamasjonsvurdering";
  }
  return navn;
}

function normalizeKategori(...cands) {
  for (const v of cands) {
    if (v == null) continue;

    if (Array.isArray(v)) {
      const first = v.find(Boolean);
      if (first) return String(first);
      continue;
    }

    if (typeof v === "object") {
      const fromObj = v.navn || v.title || v.label || v.kategori || v.category;
      if (fromObj) return String(fromObj);
      continue;
    }

    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function normalizeStoreName(value) {
  const s = String(value || "").trim().toLowerCase();

  if (!s) return "";
  if (s.includes("røyken") || s.includes("royken")) return "Røyken";
  if (s.includes("slemmestad")) return "Slemmestad";

  return String(value || "").trim();
}

function resolveStoreLabel(...cands) {
  for (const v of cands) {
    if (!v) continue;

    const s = String(v).trim();
    if (!s) continue;

    const low = s.toLowerCase();

    if (
      low.includes("røyken") ||
      low.includes("royken") ||
      low.includes("sport1royken")
    ) {
      return "Røyken";
    }

    if (
      low.includes("slemmestad") ||
      low.includes("sport1slemmestad")
    ) {
      return "Slemmestad";
    }
  }

  return "";
}

function getFerdigStatus(dateValue) {
  if (!dateValue) return "missing";

  const today = new Date();
  const target = new Date(dateValue);

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target - today;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 0) return "overdue";
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";

  return "normal";
}

function getDaysFromToday(dateValue) {
  if (!dateValue) return null;

  const today = new Date();
  const target = new Date(dateValue);

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function isDeliveryOverdue(dateValue, status) {
  if (!dateValue) return false;

  const normalizedStatus = String(status || "").trim().toLowerCase();

  // Marker kun når raden fortsatt er en booking / ikke levert inn enda
  if (normalizedStatus !== "booking") return false;

  const today = new Date();
  const target = new Date(dateValue);

  if (isNaN(+target)) return false;

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return target < today;
}

function sortByNearestDate(a, b) {
  const da = a.forventetFerdigDato ? new Date(a.forventetFerdigDato) : null;
  const db = b.forventetFerdigDato ? new Date(b.forventetFerdigDato) : null;

  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;

  return da - db;
}

function sortByNewestCreated(a, b) {
  const da = a.createdAt ? new Date(a.createdAt) : new Date(0);
  const db = b.createdAt ? new Date(b.createdAt) : new Date(0);
  return db - da;
}

function garantiFirst(fn) {
  return (a, b) => {
    const ag = !!a.garantihenvendelse;
    const bg = !!b.garantihenvendelse;
    if (ag !== bg) return ag ? -1 : 1;
    return fn(a, b);
  };
}

function formatNOK(n) {
  const val = Number(n) || 0;
  return new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(val);
}

function formatMinutes(min) {
  const m = Math.max(0, Number(min) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} t ${r} min`;
  if (h) return `${h} t`;
  return `${r} min`;
}

function parseDateSafe(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt) ? dt : null;
}

function fbCount(tjeneste) {
  const fb = Array.isArray(tjeneste?.foranBak)
    ? tjeneste.foranBak.map((v) => String(v).toLowerCase())
    : [];
  const hasForan = fb.includes("foran");
  const hasBak = fb.includes("bak");
  const c = (hasForan ? 1 : 0) + (hasBak ? 1 : 0);
  return c || 1;
}

function isDekkSlangeOnlyService(serviceLines) {
  const lines = Array.isArray(serviceLines) ? serviceLines : [];
  if (!lines.length) return false;

  return lines.every((l) => {
    const typer = Array.isArray(l?.servicetyper)
      ? l.servicetyper.map((t) => String(t).toLowerCase())
      : [];
    if (typer.includes("dekkslangeskift")) return true;

    const navn = String(l?.navn || "").toLowerCase();
    const slug = String(l?.slug || "").toLowerCase();
    return (
      navn.includes("dekkskift") ||
      navn.includes("slangeskift") ||
      slug.includes("dekk-skift") ||
      slug.includes("dekk-slangeskift") ||
      slug.includes("slange-skift")
    );
  });
}

function CommentTooltip({ tooltip }) {
  if (!tooltip.visible) return null;
  return (
    <div
      className="fixed z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-3 text-sm pointer-events-none max-w-xs max-h-96 overflow-y-auto"
      style={{ top: tooltip.y, left: tooltip.x }}
    >
      <div className="font-semibold text-slate-700 mb-2">Arbeid registrert</div>
      {tooltip.arbeid.length === 0 ? (
        <div className="text-slate-500 italic">Ingen arbeid registrert</div>
      ) : (
        <ul className="space-y-1">
          {tooltip.arbeid.map((a, i) => (
            <li key={i} className="text-slate-800 flex items-baseline justify-between gap-2">
              <span>{a.navn}</span>
              <span className="text-xs text-slate-600 whitespace-nowrap">
                {a.tidsbruk > 0 ? `${a.tidsbruk} min` : ""}
                {a.tidsbruk > 0 && a.pris > 0 ? " · " : ""}
                {a.pris > 0 ? `${a.pris.toLocaleString("nb-NO")} kr` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="font-semibold text-slate-700 mt-3 mb-2 pt-2 border-t border-slate-100">Kommentarer</div>
      {tooltip.comments.length === 0 ? (
        <div className="text-slate-500 italic">Ingen kommentarer registrert</div>
      ) : (
        tooltip.comments.map((c, i) => (
          <div key={i} className={i > 0 ? "mt-2 pt-2 border-t border-slate-100" : ""}>
            <div className="text-xs text-slate-600 mb-0.5">{c.label}</div>
            <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
          </div>
        ))
      )}
    </div>
  );
}

function useRowTooltip() {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, comments: [], arbeid: [] });

  const showTooltip = (e, comments, arbeid) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const flipUp = rect.bottom + 220 > window.innerHeight;
    setTooltip({
      visible: true,
      x: Math.min(rect.left + 12, window.innerWidth - 330),
      y: flipUp ? Math.max(8, rect.top - 220) : rect.bottom + 4,
      comments: comments || [],
      arbeid: arbeid || [],
    });
  };

  const hideTooltip = () => setTooltip((t) => ({ ...t, visible: false }));

  return { tooltip, showTooltip, hideTooltip };
}

function TableSection({ title, rows, navigate }) {
  const { tooltip, showTooltip, hideTooltip } = useRowTooltip();
  return (
    <div className="space-y-2">
      <CommentTooltip tooltip={tooltip} />
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{rows.length} stk</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-left">
          <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
            <tr>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Ref</th>
              <th className="px-3 py-1.5 text-left">Produkt</th>
              <th className="px-3 py-1.5 text-left">Kategori</th>
              <th className="px-3 py-1.5 text-left">Butikk</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Booking</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Ferdig</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Status</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Opprettet</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-slate-200 hover:bg-emerald-50/50 cursor-pointer"
                onClick={() => navigate(`/workshop/${r.id}`)}
                onMouseEnter={(e) => showTooltip(e, r.comments, r.arbeid)}
                onMouseLeave={hideTooltip}
              >
                <td className="px-3 py-1.5 font-mono hidden md:table-cell">
                  {r.serviceRef}
                </td>

                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    {r.butikkmodus && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-indigo-400 shrink-0"
                        title="Lagt inn i butikk"
                      />
                    )}
                    {r.produkt.merke}
                    {r.produkt.type ? ` – ${renameTjeneste(r.produkt.type)}` : ""}
                    {r.produkt.farge ? ` (${r.produkt.farge})` : ""}
                  </span>
                </td>
                <td className="px-3 py-1.5">{r.produkt.kategori}</td>
                <td className="px-3 py-1.5">{r.produkt.butikkKort}</td>

<td className="px-3 py-1.5 hidden md:table-cell">
  <div className="flex items-center gap-1.5 flex-wrap">
    <span>{r.bookingType}</span>
    {r.garantihenvendelse && (
      <span className="inline-flex items-center rounded-full bg-red-700 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide">
        REKLAMASJON
      </span>
    )}
    {r.prisoverslag && r.status !== "Tilbud godkjent" && (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-yellow-100 border border-yellow-400 px-2 py-0.5 text-yellow-900 text-[10px] font-bold tracking-wide">
        💰 PRISTILBUD
      </span>
    )}
    {r.prioritering?.haster && !r.garantihenvendelse && (
      <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide">
        HASTER
      </span>
    )}
    {r.dekkSlangeOnly && (
      <span
        className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide"
        title="Kun dekk-/slangeskift – rask jobb, ikke la den drukne i listen"
      >
        🛞 KUN DEKK/SLANGE
      </span>
    )}
  </div>
</td>

                <td className="px-3 py-1.5 hidden md:table-cell">
                  {r.onsketFerdigDatoText && (
                    <div
                      className="mb-1 inline-block rounded-lg bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800"
                      title="Kunden har satt ønsket ferdigdato"
                    >
                      Ønsket: {r.onsketFerdigDatoText}
                    </div>
                  )}
                  {(() => {
                    const ferdigStatus = getFerdigStatus(r.forventetFerdigDato);

                    // Estimert, ikke bekreftet ferdigdato: gul i stedet for rød.
                    if (r.ferdigDatoEstimert && ["overdue", "today", "tomorrow"].includes(ferdigStatus)) {
                      const gul = {
                        overdue: "text-yellow-950 bg-yellow-300 font-bold px-2 py-0.5 rounded-lg",
                        today: "text-yellow-900 bg-yellow-100 font-bold px-2 py-0.5 rounded-lg",
                        tomorrow: "text-yellow-800 font-semibold",
                      };
                      return (
                        <span
                          className={gul[ferdigStatus]}
                          title="Estimert ferdigdato, satt automatisk. Ikke bekreftet (ønsket ferdigdato er ikke satt)."
                        >
                          {r.forventetFerdigDatoText}
                        </span>
                      );
                    }

                    if (ferdigStatus === "missing") {
                      return (
                        <div className="flex items-center gap-2 text-slate-500">
                          <span>—</span>
                          <span
                            className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 font-bold text-sm"
                            title="Ingen ferdigdato registrert"
                          >
                            !
                          </span>
                        </div>
                      );
                    }

                    if (ferdigStatus === "overdue") {
                      return (
                        <span className="text-white bg-red-600 font-bold px-2 py-0.5 rounded-lg">
                          {r.forventetFerdigDatoText}
                        </span>
                      );
                    }

                    if (ferdigStatus === "today") {
                      return (
                        <span className="text-red-700 font-bold bg-red-100 px-2 py-0.5 rounded-lg">
                          {r.forventetFerdigDatoText}
                        </span>
                      );
                    }

                    if (ferdigStatus === "tomorrow") {
                      return (
                        <span className="text-red-600 font-semibold">
                          {r.forventetFerdigDatoText}
                        </span>
                      );
                    }

                    return r.forventetFerdigDatoText;
                  })()}
                </td>

                <td className="px-3 py-1.5 hidden md:table-cell">
                  <div className="flex items-center gap-2">
                    <span>{r.status}</span>
                    {r.unreadCustomerCount > 0 && (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                        💬 {r.unreadCustomerCount}
                      </span>
                    )}
                  </div>
                </td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {r.createdAtText}
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-slate-600"
                >
                  Ingen treff
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingTableSection({ title, rows, navigate, showAll, onToggleShowAll, futurePendingCount = 0 }) {
  const { tooltip, showTooltip, hideTooltip } = useRowTooltip();
  return (
    <div className="space-y-2">
      <CommentTooltip tooltip={tooltip} />
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{rows.length} stk</span>
          {onToggleShowAll && (
            <button
              type="button"
              onClick={onToggleShowAll}
              className="inline-flex min-h-[2.25rem] items-center rounded-lg border-2 border-slate-300 bg-white px-2.5 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              {showAll
                ? "Vis kun i dag og eldre"
                : `Vis neste 6 dager${futurePendingCount ? ` (${futurePendingCount})` : ""}`}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-left">
          <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
            <tr>
              <th className="px-3 py-1.5 text-left">Kunde</th>
              <th className="px-3 py-1.5 text-left">Butikk</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Status</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Antall</th>
              <th className="px-3 py-1.5 text-right">Pris</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Tidsestimat</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Leveringsdato</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Estimert ferdig</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Opprettet</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((b) => {
              const todayMidnight = new Date();
              todayMidnight.setHours(0, 0, 0, 0);
              const dRaw = b.deliveryRaw ? new Date(b.deliveryRaw) : null;
              if (dRaw) dRaw.setHours(0, 0, 0, 0);
              const isOverdue = dRaw !== null && dRaw < todayMidnight;

              return (
              <tr
                key={b.id}
                className={`border-t cursor-pointer ${isOverdue ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}`}
                onClick={() => navigate(`/booking/${b.id}`)}
                onMouseEnter={(e) => showTooltip(e, b.comments)}
                onMouseLeave={hideTooltip}
              >
                <td className="px-3 py-1.5">
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 inline-flex items-center gap-1.5">
                      {b.butikkmodus && (
                        <span
                          className="inline-block w-2 h-2 rounded-full bg-indigo-400 shrink-0"
                          title="Lagt inn i butikk"
                        />
                      )}
                      {b.customerName || "—"}
                    </span>
                    <span className="text-sm text-slate-600">
                      {b.phone || "—"}
                    </span>
                  </div>
                </td>

                <td className="px-3 py-1.5">{b.storeNameKort}</td>

                <td className="px-3 py-1.5 hidden md:table-cell">
  <div className="flex items-center gap-2">
    <span>{b.status}</span>
    {b.unreadCustomerCount > 0 && (
      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
        💬 {b.unreadCustomerCount}
      </span>
    )}
  </div>
</td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {b.totalItems}
                </td>

                <td className="px-3 py-1.5 text-right">{formatNOK(b.totalPrice)}</td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {formatMinutes(b.totalMinutes)}
                </td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {isOverdue ? (
                    <span
                      className="text-white bg-red-600 font-bold px-2 py-0.5 rounded-lg"
                      title="Leveringsdato er passert — ikke levert inn enda"
                    >
                      {b.leveringsDato || "—"}
                    </span>
                  ) : (
                    b.leveringsDato || "—"
                  )}
                </td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {b.estimatedFinish || "—"}
                </td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {b.createdAt}
                </td>
              </tr>
              );
            })}

            {!rows.length && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-slate-600"
                >
                  Ingen treff
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LaterCombinedTableSection({ title, rows, navigate }) {
  const { tooltip, showTooltip, hideTooltip } = useRowTooltip();
  return (
    <div className="space-y-2">
      <CommentTooltip tooltip={tooltip} />
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{rows.length} stk</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-left">
          <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
            <tr>
              <th className="px-3 py-1.5 text-left">Ref / Navn</th>
              <th className="px-3 py-1.5 text-left">Produkt</th>
              <th className="px-3 py-1.5 text-left">Kategori</th>
              <th className="px-3 py-1.5 text-left">Butikk</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Booking</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Leveringsdato</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Ferdig</th>
              <th className="px-3 py-1.5 text-left hidden md:table-cell">Status</th>
              <th className="px-3 py-1.5 text-right hidden md:table-cell">Opprettet</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.source}-${r.id}`}
                className="border-t border-slate-200 hover:bg-emerald-50/50 cursor-pointer"
                onClick={() =>
                  navigate(
                    r.source === "booking"
                      ? `/booking/${r.id}`
                      : `/workshop/${r.id}`
                  )
                }
                onMouseEnter={(e) => showTooltip(e, r.comments, r.arbeid)}
                onMouseLeave={hideTooltip}
              >
                <td className="px-3 py-1.5 font-mono">
                  {r.refOrName || "—"}
                </td>

                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    {r.butikkmodus && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-indigo-400 shrink-0"
                        title="Lagt inn i butikk"
                      />
                    )}
                    {r.productText || "—"}
                  </span>
                </td>
                <td className="px-3 py-1.5">{r.categoryText || "—"}</td>
                <td className="px-3 py-1.5">{r.storeText || "—"}</td>

                <td className="px-3 py-1.5 hidden md:table-cell">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{r.bookingTypeText || "—"}</span>
                    {r.garantihenvendelse && (
                      <span className="inline-flex items-center rounded-full bg-red-700 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide">
                        REKLAMASJON
                      </span>
                    )}
                    {r.prisoverslag && r.statusText !== "Tilbud godkjent" && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-yellow-100 border border-yellow-400 px-2 py-0.5 text-yellow-900 text-[10px] font-bold tracking-wide">
                        💰 PRISTILBUD
                      </span>
                    )}
                    {r.dekkSlangeOnly && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide"
                        title="Kun dekk-/slangeskift – rask jobb, ikke la den drukne i listen"
                      >
                        🛞 KUN DEKK/SLANGE
                      </span>
                    )}
                  </div>
                </td>

<td className="px-3 py-1.5 hidden md:table-cell">
  {isDeliveryOverdue(r.deliveryRaw, r.statusText) ? (
    <span
      className="text-red-700 font-semibold bg-red-50 px-2 py-0.5 rounded-lg"
      title="Leveringsdato er passert uten at produktet er levert inn"
    >
      {r.deliveryText || "—"}
    </span>
  ) : (
    r.deliveryText || "—"
  )}
</td>

                <td className="px-3 py-1.5 hidden md:table-cell">
                  {r.finishText || "—"}
                </td>

<td className="px-3 py-1.5 hidden md:table-cell">
  <div className="flex items-center gap-2">
    <span>{r.statusText || "—"}</span>
    {r.unreadCustomerCount > 0 && (
      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
        💬 {r.unreadCustomerCount}
      </span>
    )}
  </div>
</td>

                <td className="px-3 py-1.5 text-right hidden md:table-cell">
                  {r.createdAtText || "—"}
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-slate-600"
                >
                  Ingen treff
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getLatestCustomerMessage(messages = []) {
  const customerMsgs = (Array.isArray(messages) ? messages : [])
    .filter((m) => m.from === "customer" && String(m.text || "").trim())
    .sort(
      (a, b) =>
        new Date(b.createdAt || b.at || 0) - new Date(a.createdAt || a.at || 0)
    );

  return customerMsgs[0] || null;
}

export default function WorkshopList() {
  const [rows, setRows] = useState([]);
  const [bookingRows, setBookingRows] = useState([]);
const [bookingRowsLater, setBookingRowsLater] = useState([]);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Alle");
  const [selectedWorkshop, setSelectedWorkshop] = useState(() => {
  const storeName = Cookies.get("selectedStore") || "";
  const storeId = Cookies.get("butikkid") || "";

  if (storeId === "1") return "Røyken";
  if (storeId === "2") return "Slemmestad";

  return normalizeStoreName(storeName) || "Alle";
});
  const navigate = useNavigate();

  const [snapOpen, setSnapOpen] = useState(false);
  const [snapDesc, setSnapDesc] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);

  useEffect(() => {
  const syncSelectedWorkshopFromCookie = () => {
    const storeName = Cookies.get("selectedStore") || "";
    const storeId = Cookies.get("butikkid") || "";

    let nextWorkshop = "Alle";

    if (storeId === "1") nextWorkshop = "Røyken";
    else if (storeId === "2") nextWorkshop = "Slemmestad";
    else nextWorkshop = normalizeStoreName(storeName) || "Alle";

    setSelectedWorkshop(nextWorkshop);
  };

  syncSelectedWorkshopFromCookie();

  window.addEventListener("storeUpdated", syncSelectedWorkshopFromCookie);

  return () => {
    window.removeEventListener("storeUpdated", syncSelectedWorkshopFromCookie);
  };
}, []);

  const [unreadModalOpen, setUnreadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchAll, setSearchAll] = useState(false);
  const [allRowsForSearch, setAllRowsForSearch] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!searchAll) { setAllRowsForSearch([]); return; }
    fetch(`${API_BASE_URL}/workshop-services`)
      .then((r) => r.json())
      .then((data) => {
        const mapped = (data || []).map((s) => ({
          id: s._id,
          source: "service",
          label: `#${s.serviceRef || "—"}`,
          product: s.produkt?.produktmerke || s.produkt?.navn || "Produkt",
          customer: [s.customerSnapshot?.firstName, s.customerSnapshot?.lastName].filter(Boolean).join(" "),
          status: s.status || "—",
          store: resolveStoreLabel(s.produkt?.butikk, s.produkt?.butikkSlug, s.butikk) || "—",
        }));
        setAllRowsForSearch(mapped);
      })
      .catch(() => setAllRowsForSearch([]));
  }, [searchAll]);

  useEffect(() => {
    const run = async () => {
      try {
        const workshopUrl =
          status && status !== "Alle"
            ? `${API_BASE_URL}/workshop-services?status=${encodeURIComponent(
                status
              )}`
            : `${API_BASE_URL}/workshop-services`;

        const [workshopRes, bookingRes] = await Promise.all([
          fetch(workshopUrl),
          fetch(`${API_BASE_URL}/bookings?status=booking`),
        ]);

        const workshopData = await workshopRes.json();
        const bookingData = await bookingRes.json();

        const norm = (workshopData || [])
          .filter((s) => {
            if (status === "Alle") {
              const st = (s.status || "").toLowerCase();
              return st !== "avsluttet" && st !== "kansellert";
            }
            return true;
          })
          .map((s) => {
            const kategori = normalizeKategori(
              s.kategori,
              s.category,
              s.produkt?.kategori,
              s.produkt?.produktkategori
            );

const bookingType =
  s.bookingType ||
  s.booking?.type ||
  s.booking?.bookingType ||
  s.produkt?.bookingType ||
  s.produkt?.bookingtype ||
  "";

const beregnetFerdigDato =
  s.produkt?.forventetFerdigDato || s.forventetFerdigDato || null;

// Ski: ønsket ferdigdato er det kunden er lovet. Beregnet dato er fortsatt
// verkstedets mål, men er ønsket dato tidligere (ansatt-overstyring), gjelder den.
const onsketFerdigDato =
  (s.produkt?.ferdigDatoValgfri && s.produkt?.onsketFerdigDato) || null;

const forventetFerdigDato =
  onsketFerdigDato &&
  (!beregnetFerdigDato || new Date(onsketFerdigDato) < new Date(beregnetFerdigDato))
    ? onsketFerdigDato
    : beregnetFerdigDato;

const forventetFerdigDatoText = forventetFerdigDato
  ? fmtDate(forventetFerdigDato)
  : "";

const leveringsDato =
  s.produkt?.leveringsDato || s.leveringsDato || null;

const leveringsDatoText = leveringsDato
  ? fmtDate(leveringsDato)
  : "";

            const messages = Array.isArray(s.messages) ? s.messages : [];
            const customerMsgs = messages.filter((m) => m.from === "customer");
            const unreadCustomerCount = customerMsgs.filter((m) => !m.readAt).length;

            const commentsParts = [
              s.produkt?.kommentar,
              s.produkt?.lastStatusComment,
              s.arbeidsbeskrivelse,
            ].filter(Boolean);

            const commentsTooltip = commentsParts.join(" \n— ");

            const serviceLineComments = (Array.isArray(s.serviceLines) ? s.serviceLines : [])
              .filter((l) => l.kommentar)
              .map((l) => ({ label: l.navn || "Servicelinje", text: l.kommentar }));

            const arbeid = (Array.isArray(s.serviceLines) ? s.serviceLines : []).map((l) => ({
              navn: renameTjeneste(l.navn || "Tjeneste", l.slug),
              tidsbruk: Number(l.tidsbruk) || 0,
              pris: Number(l.pris) || 0,
            }));

            const comments = [
              s.produkt?.kommentar && { label: "Kommentar", text: s.produkt.kommentar },
              s.produkt?.mottakKommentar && { label: "Kommentar ved mottak", text: s.produkt.mottakKommentar },
              s.arbeidsbeskrivelse && { label: "Arbeidsbeskrivelse", text: s.arbeidsbeskrivelse },
              s.produkt?.lastStatusComment && { label: "Siste statuskommentar", text: s.produkt.lastStatusComment },
              ...serviceLineComments,
            ].filter(Boolean);
const butikkKort =
  resolveStoreLabel(
    s.produkt?.butikk,
    s.produkt?.verksted,
    s.produkt?.butikkSlug,
    s.produkt?.verkstedSlug,
    s.butikk,
    s.verksted,
    s.butikkSlug,
    s.verkstedSlug
  ) || "—";

const butikk = butikkKort;

            return {
              id: s._id,
              serviceRef: s.serviceRef || "—",
              status: s.status || "—",
              butikkmodus: !!s.butikkmodus,
              createdAt: s.createdAt || null,
              createdAtText: fmtDate(s.createdAt),
              messages,


              produkt: {
                merke: s.produkt?.produktmerke || s.produkt?.navn || "Produkt",
                type: s.produkt?.produkttype || "",
                farge: s.produkt?.farge || "",
                kategori: kategori || "—",
                butikk,
                butikkKort,
              },
              arbeid,
              dekkSlangeOnly: isDekkSlangeOnlyService(s.serviceLines),

bookingType: bookingType || "—",
leveringsDato,
leveringsDatoText: leveringsDatoText || "—",
forventetFerdigDato,
forventetFerdigDatoText: forventetFerdigDatoText || "—",
onsketFerdigDatoText: onsketFerdigDato ? fmtDate(onsketFerdigDato) : "",
// Ski med «Ønsket ferdig: Dato ikke satt»: datoen er beregnet automatisk og ikke lovet kunden.
// Vises gul i stedet for rød til ønsket ferdigdato settes (ved mottak eller i WorkshopDetails).
ferdigDatoEstimert: Boolean(s.produkt?.ferdigDatoValgfri && !onsketFerdigDato && beregnetFerdigDato),
prioritering: s.produkt?.prioritering || null,
garantihenvendelse:
              s.produkt?.garantihenvendelse ||
              (s.serviceLines?.some(
                (l) => String(l.slug || "").toLowerCase() === "garantihenvendelse"
              )
                ? true
                : null),
prisoverslag: !!s.produkt?.prisoverslag,

              customerName: [s.customerSnapshot?.firstName, s.customerSnapshot?.lastName]
                .filter(Boolean).join(" "),
              unreadCustomerCount,
              commentsTooltip,
              comments,
            };
          });

const allNormalizedBookings = (bookingData || [])
  .filter((b) => !b.convertedToServicesAt)
  .map((b) => {
    let totalPrice = 0;
    let totalMinutes = 0;
    let latestFinish = null;
    let earliestDelivery = null;
    let storeName = "";
    let firstProductName = "";
    let firstCategory = "";

    const convertedItemIndexes = new Set(
      Array.isArray(b.convertedItemIndexes) ? b.convertedItemIndexes : []
    );
    // Items som allerede er registrert som mottatt (konvertert til service)
    // skal ikke telle med — bookingen kan ha andre varer som fortsatt venter.
    const items = (Array.isArray(b.items) ? b.items : []).filter(
      (_, idx) => !convertedItemIndexes.has(idx)
    );

    for (const prod of items) {
if (!storeName) {
  storeName =
    resolveStoreLabel(
      prod?.butikk,
      prod?.verksted,
      prod?.butikkSlug,
      prod?.verkstedSlug
    ) || "";
}

      if (!firstProductName) {
        firstProductName =
          prod?.produktmerke ||
          prod?.navn ||
          prod?.produkttype ||
          prod?.produktType ||
          "Booking";
      }

      if (!firstCategory) {
        firstCategory = normalizeKategori(
          prod?.kategori,
          prod?.category,
          prod?.produkttype,
          prod?.produktType
        );
      }

      const levering = parseDateSafe(prod?.leveringsDato);
      if (levering && (!earliestDelivery || levering < earliestDelivery)) {
        earliestDelivery = levering;
      }

      const ferdig = parseDateSafe(prod?.forventetFerdigDato);
      if (ferdig && (!latestFinish || ferdig > latestFinish)) {
        latestFinish = ferdig;
      }

      const tlist = Array.isArray(prod?.items)
        ? prod.items
        : Array.isArray(prod?.tjenester)
        ? prod.tjenester
        : [];

      for (const t of tlist) {
        if (typeof t?.pris === "number") {
          totalPrice += (Number(t.pris) || 0) * fbCount(t);
        }
        if (typeof t?.tidsbruk === "number") {
          totalMinutes += Math.max(0, Number(t.tidsbruk) || 0);
        }
      }
    }

    const firstName = b.customer?.firstName || "";
    const lastName = b.customer?.lastName || "";
    const customerName = `${firstName} ${lastName}`.trim();

    const messages = Array.isArray(b.messages) ? b.messages : [];
const customerMsgs = messages.filter((m) => m.from === "customer");
const unreadCustomerCount = customerMsgs.filter((m) => !m.readAt).length;

const comments = items.flatMap((prod, idx) => {
  const suffix = items.length > 1 ? ` (vare ${idx + 1})` : "";
  const tjenester = Array.isArray(prod?.items)
    ? prod.items
    : Array.isArray(prod?.tjenester)
    ? prod.tjenester
    : [];
  const lineComments = tjenester
    .filter((t) => t.kommentar)
    .map((t) => ({ label: `${t.navn || "Linje"}${suffix}`, text: t.kommentar }));
  return [
    prod.kommentar && { label: `Kommentar${suffix}`, text: prod.kommentar },
    prod.mottakKommentar && { label: `Mottak${suffix}`, text: prod.mottakKommentar },
    ...lineComments,
  ].filter(Boolean);
});

return {
  id: b._id,
  source: "booking",
  status: b.status || "booking",
  butikkmodus: !!b.butikkmodus,
  createdAt: b.createdAt ? fmtDate(b.createdAt) : "",
  createdAtRaw: b.createdAt || null,
  messages,


  firstName,
  lastName,
  customerName,
  phone: b.customer?.phoneNumber || "",

  storeName: storeName || "—",
  storeNameKort: normalizeStoreName(storeName || "—"),
  totalItems: items.length,
  totalPrice,
  totalMinutes,

  leveringsDato: earliestDelivery ? fmtDate(earliestDelivery) : "",
  leveringsDatoRaw: earliestDelivery
    ? earliestDelivery.toISOString()
    : null,
  deliveryRaw: earliestDelivery ? new Date(earliestDelivery) : null,

  estimatedFinish: latestFinish ? fmtDate(latestFinish) : "",
  estimatedFinishRaw: latestFinish ? latestFinish.toISOString() : null,

  productText: firstProductName || "Booking",
  categoryText: firstCategory || "—",
  storeText: normalizeStoreName(storeName || "—"),
  bookingTypeText:
    b.bookingType ||
    b.booking?.type ||
    b.booking?.bookingType ||
    items[0]?.bookingType ||
    items[0]?.bookingtype ||
    "—",
  deliveryText: earliestDelivery ? fmtDate(earliestDelivery) : "—",
  finishText: latestFinish ? fmtDate(latestFinish) : "—",
  statusText: b.status || "booking",
  createdAtText: b.createdAt ? fmtDate(b.createdAt) : "",

  unreadCustomerCount,
  comments,
};
  });

const normalizedBookings = allNormalizedBookings
  .filter((b) => {
    if (!b.deliveryRaw) return false;
    const days = getDaysFromToday(b.deliveryRaw);
    return days !== null && days <= 6;
  })
  .sort((a, b) => {
    const da = a.deliveryRaw;
    const db = b.deliveryRaw;
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
  });

const normalizedBookingsLater = allNormalizedBookings
  .filter((b) => {
    const days = getDaysFromToday(b.estimatedFinishRaw);
    return days != null && days > 7;
  })
  .sort((a, b) => {
    const da = a.estimatedFinishRaw ? new Date(a.estimatedFinishRaw) : null;
    const db = b.estimatedFinishRaw ? new Date(b.estimatedFinishRaw) : null;
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
  });

setRows(norm);
setBookingRows(normalizedBookings);
setBookingRowsLater(normalizedBookingsLater);

      } catch (e) {
        console.error("FEIL /workshop-services eller /bookings:", e);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [status]);

const workshopOptions = useMemo(() => {
  const names = new Set();

  rows.forEach((r) => {
    if (r?.produkt?.butikkKort && r.produkt.butikkKort !== "—") {
      names.add(r.produkt.butikkKort);
    }
  });

  bookingRows.forEach((b) => {
    if (b?.storeNameKort && b.storeNameKort !== "—") {
      names.add(b.storeNameKort);
    }
  });

  bookingRowsLater.forEach((b) => {
    if (b?.storeNameKort && b.storeNameKort !== "—") {
      names.add(b.storeNameKort);
    }
  });

  return ["Alle", ...Array.from(names).sort((a, b) => a.localeCompare(b, "no"))];
}, [rows, bookingRows, bookingRowsLater]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (selectedWorkshop === "Alle") return true;
      return r.produkt.butikkKort === selectedWorkshop;
    });
  }, [rows, selectedWorkshop]);

  const filteredBookingRows = useMemo(() => {
    return bookingRows.filter((b) => {
      if (selectedWorkshop !== "Alle" && b.storeNameKort !== selectedWorkshop) return false;
      if (!showAllBookings) {
        const days = getDaysFromToday(b.deliveryRaw);
        return days !== null && days <= 0;
      }
      return true;
    });
  }, [bookingRows, selectedWorkshop, showAllBookings]);

  const futurePendingCount = useMemo(() => {
    return bookingRows.filter((b) => {
      if (selectedWorkshop !== "Alle" && b.storeNameKort !== selectedWorkshop) return false;
      const days = getDaysFromToday(b.deliveryRaw);
      return days !== null && days > 0 && days <= 6;
    }).length;
  }, [bookingRows, selectedWorkshop]);

  const filteredBookingRowsLater = useMemo(() => {
  return bookingRowsLater.filter((b) => {
    if (selectedWorkshop === "Alle") return true;
    return b.storeNameKort === selectedWorkshop;
  });
}, [bookingRowsLater, selectedWorkshop]);

  const grouped = useMemo(() => {
    const withoutDate = [];
    const next7Days = [];
    const later = [];

    filtered.forEach((r) => {
      const days = getDaysFromToday(r.forventetFerdigDato);

      if (days == null) {
        withoutDate.push(r);
      } else if (days <= 7) {
        next7Days.push(r);
      } else {
        later.push(r);
      }
    });

    withoutDate.sort(garantiFirst(sortByNewestCreated));
    next7Days.sort(garantiFirst(sortByNearestDate));
    later.sort(garantiFirst(sortByNearestDate));

    return {
      withoutDate,
      next7Days,
      later,
    };
  }, [filtered]);

const laterCombinedRows = useMemo(() => {
const serviceLaterRows = grouped.later.map((r) => ({
  id: r.id,
  source: "service",
  refOrName: r.serviceRef || "—",
  productText:
    (r.produkt?.merke || "Produkt") +
    (r.produkt?.type ? ` – ${renameTjeneste(r.produkt.type)}` : "") +
    (r.produkt?.farge ? ` (${r.produkt.farge})` : ""),
  categoryText: r.produkt?.kategori || "—",
  storeText: r.produkt?.butikkKort || "—",
  bookingTypeText: r.bookingType || "—",
  deliveryText: r.leveringsDatoText || "—",
  deliveryRaw: r.leveringsDato ? new Date(r.leveringsDato) : null,
  finishText: r.forventetFerdigDatoText || "—",
  statusText: r.status || "—",
  createdAtText: r.createdAtText || "—",
  finishRaw: r.forventetFerdigDato ? new Date(r.forventetFerdigDato) : null,
  unreadCustomerCount: Number(r.unreadCustomerCount) || 0,
  comments: r.comments || [],
  arbeid: r.arbeid || [],
  garantihenvendelse: r.garantihenvendelse || null,
  prisoverslag: !!r.prisoverslag,
  dekkSlangeOnly: !!r.dekkSlangeOnly,
  butikkmodus: !!r.butikkmodus,
}));

const bookingLaterRows = filteredBookingRowsLater.map((b) => ({
  id: b.id,
  source: "booking",
  refOrName: b.customerName || "—",
  productText: b.productText || "Booking",
  categoryText: b.categoryText || "—",
  storeText: b.storeText || "—",
  bookingTypeText: b.bookingTypeText || "—",
  deliveryText: b.deliveryText || "—",
  deliveryRaw: b.deliveryRaw || null,
  finishText: b.finishText || "—",
  statusText: b.statusText || "booking",
  createdAtText: b.createdAtText || "—",
  finishRaw: b.estimatedFinishRaw ? new Date(b.estimatedFinishRaw) : null,
  unreadCustomerCount: Number(b.unreadCustomerCount) || 0,
  comments: b.comments || [],
  butikkmodus: !!b.butikkmodus,
}));

  return [...serviceLaterRows, ...bookingLaterRows].sort(garantiFirst((a, b) => {
    if (!a.finishRaw && !b.finishRaw) return 0;
    if (!a.finishRaw) return 1;
    if (!b.finishRaw) return -1;
    return a.finishRaw - b.finishRaw;
  }));
}, [grouped.later, filteredBookingRowsLater]);

const totalUnreadWorkshopMessages = useMemo(() => {
  return rows.reduce((sum, r) => sum + (Number(r.unreadCustomerCount) || 0), 0);
}, [rows]);

const workshopServicesWithUnread = useMemo(() => {
  return rows.filter((r) => (Number(r.unreadCustomerCount) || 0) > 0).length;
}, [rows]);

const totalUnreadBookingMessages = useMemo(() => {
  return [...bookingRows, ...bookingRowsLater].reduce(
    (sum, b) => sum + (Number(b.unreadCustomerCount) || 0),
    0
  );
}, [bookingRows, bookingRowsLater]);

const bookingsWithUnread = useMemo(() => {
  return [...bookingRows, ...bookingRowsLater].filter(
    (b) => (Number(b.unreadCustomerCount) || 0) > 0
  ).length;
}, [bookingRows, bookingRowsLater]);

const totalUnreadMessages = totalUnreadWorkshopMessages + totalUnreadBookingMessages;
const totalItemsWithUnread = workshopServicesWithUnread + bookingsWithUnread;

const unreadItems = useMemo(() => {
  const workshopUnread = rows
    .filter((r) => (Number(r.unreadCustomerCount) || 0) > 0)
    .map((r) => {
      const latestMsg = getLatestCustomerMessage(r.messages);

      return {
        id: r.id,
        source: "workshop",
        unreadCount: Number(r.unreadCustomerCount) || 0,
        title: `#${r.serviceRef || "—"} · ${r.produkt?.merke || "Produkt"}`,
        subtitle: [
          r.produkt?.butikkKort || "—",
          r.status || "—",
          r.forventetFerdigDatoText || "Ingen ferdigdato",
        ]
          .filter(Boolean)
          .join(" · "),
        latestMessageText: latestMsg?.text || "",
        latestMessageAt: latestMsg?.createdAt || latestMsg?.at || "",
      };
    });

  const bookingUnread = [...bookingRows, ...bookingRowsLater]
    .filter((b) => (Number(b.unreadCustomerCount) || 0) > 0)
    .map((b) => {
      const latestMsg = getLatestCustomerMessage(b.messages);

      return {
        id: b.id,
        source: "booking",
        unreadCount: Number(b.unreadCustomerCount) || 0,
        title: `${b.customerName || "—"} · ${b.productText || "Booking"}`,
        subtitle: [
          b.storeNameKort || "—",
          b.status || "booking",
          b.finishText || "Ingen ferdigdato",
        ]
          .filter(Boolean)
          .join(" · "),
        latestMessageText: latestMsg?.text || "",
        latestMessageAt: latestMsg?.createdAt || latestMsg?.at || "",
      };
    });

  return [...workshopUnread, ...bookingUnread].sort(
    (a, b) => b.unreadCount - a.unreadCount
  );
}, [rows, bookingRows, bookingRowsLater]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return [];

    const serviceSource = searchAll
      ? allRowsForSearch
      : rows.map((r) => ({
          id: r.id,
          source: "service",
          label: `#${r.serviceRef}`,
          product: r.produkt?.merke || "—",
          customer: r.customerName || "",
          status: r.status,
          store: r.produkt?.butikkKort || "—",
        }));

    const allBookings = [...bookingRows, ...bookingRowsLater].map((b) => ({
      id: b.id,
      source: "booking",
      label: b.customerName || "—",
      product: b.productText || "—",
      customer: b.customerName || "",
      status: b.statusText || "booking",
      store: b.storeNameKort || "—",
    }));

    return [...serviceSource, ...allBookings]
      .filter((item) => {
        const haystack = `${item.customer} ${item.product} ${item.label}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 15);
  }, [searchQuery, searchAll, allRowsForSearch, rows, bookingRows, bookingRowsLater]);

  async function createSnap() {
    setSnapBusy(true);
    try {
      const storeId = Cookies.get("butikkid") || "";
      const butikk = storeId === "1" ? "Røyken" : storeId === "2" ? "Slemmestad" : selectedWorkshop;
      const butikkSlug = storeId === "1" ? "sport1royken" : storeId === "2" ? "sport1slemmestad" : "";
      const res = await fetch(`${API_BASE_URL}/workshop-services/snap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ arbeidsbeskrivelse: snapDesc.trim(), butikk, butikkSlug }),
      });
      if (!res.ok) throw new Error("Feil fra server");
      const ws = await res.json();
      setSnapOpen(false);
      setSnapDesc("");
      navigate(`/workshop/${ws._id}`);
    } catch (e) {
      console.error(e);
      alert("Kunne ikke opprette snap-service");
    } finally {
      setSnapBusy(false);
    }
  }

  const revenueStoreSlug =
    selectedWorkshop === "Røyken"
      ? "sport1royken"
      : selectedWorkshop === "Slemmestad"
      ? "sport1slemmestad"
      : "alle";

  if (loading) return <div className="py-10 text-center">Laster services…</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />

      <Sidetopp ikon="liste" farge="verksted" tittel="Sykler i verkstedet" undertittel="Serviceliste og status">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => { setSnapDesc(""); setSnapOpen(true); }} className={knappPrimar}>
            <Ikon navn="pluss" className="w-5 h-5" /> Snap-service
          </button>
          <button type="button" onClick={() => navigate("/workshop-statistikk")} className={knappSekundar}>
            <Ikon navn="diagram" className="w-5 h-5" /> Se statistikk
          </button>
        </div>
      </Sidetopp>

      {/* Søk, filtre og dagens omsetning */}
      <section aria-label="Søk og filtre" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div ref={searchRef} className="relative w-full md:flex-1">
            <div className="flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-600/30">
              <Ikon navn="sok" className="w-5 h-5 shrink-0 text-slate-500" />
              <input
                type="search"
                aria-label="Søk på produkt eller kunde"
                placeholder="Søk på produkt eller kunde…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSearchQuery("")}
                className="min-w-0 flex-1 bg-transparent text-base outline-none"
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery("")} aria-label="Tøm søket" className="rounded-lg px-1.5 text-lg leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-800">×</button>
              )}
            </div>

            <label className="mt-1.5 ml-1 flex w-fit cursor-pointer select-none items-center gap-2">
              <input
                type="checkbox"
                checked={searchAll}
                onChange={(e) => setSearchAll(e.target.checked)}
                className="rounded-lg border-slate-300 text-indigo-700"
              />
              <span className="text-sm text-slate-700">Inkluder avsluttede og kansellerte</span>
            </label>

            {searchQuery.trim().length >= 2 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                {searchResults.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-slate-600 italic">Ingen treff</div>
                ) : (
                  <ul>
                    {searchResults.map((item) => (
                      <li key={`${item.source}-${item.id}`}>
                        <button
                          type="button"
                          className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                          onClick={() => {
                            navigate(item.source === "booking" ? `/booking/${item.id}` : `/workshop/${item.id}`);
                            setSearchQuery("");
                          }}
                        >
                          <span className="text-base">{item.source === "booking" ? "📋" : "🔧"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-slate-900">{item.label}</span>
                              <span className="text-sm text-slate-700 truncate">{item.product}</span>
                              {item.customer && item.source === "service" && (
                                <span className="text-xs text-slate-600">{item.customer}</span>
                              )}
                              <span className="text-xs px-1.5 py-0.5 rounded-lg bg-slate-100 text-slate-600 font-medium">{item.store}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">{item.status}</div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
              )}
            </div>
          )}
          </div>

          <div className="w-full md:flex-1">
            <WorkshopTodayRevenueBar storeSlug={revenueStoreSlug} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Valgfelt id="ws-status" etikett="Status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
            <option>Alle</option>
            <option>mottatt</option>
            <option>avsluttet</option>
            <option>kansellert</option>
          </Valgfelt>
          <Valgfelt id="ws-verksted" etikett="Verksted" value={selectedWorkshop} onChange={(e) => setSelectedWorkshop(e.target.value)} className="w-56">
            {workshopOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Valgfelt>
        </div>
      </section>

{totalUnreadMessages > 0 && (
  <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2.5">
    <div>
      <div className="font-semibold text-red-800">
        Du har uleste kundemeldinger
      </div>

      <div className="text-sm text-red-700 mt-1">
        {totalItemsWithUnread} ordre{totalItemsWithUnread !== 1 ? "r" : ""} har totalt{" "}
        {totalUnreadMessages} ulest{totalUnreadMessages !== 1 ? "e" : ""} melding
        {totalUnreadMessages !== 1 ? "er" : ""}.
      </div>

      <div className="text-xs text-red-700 mt-1">
        {workshopServicesWithUnread > 0 && (
          <span>
            Verksted: {workshopServicesWithUnread} med {totalUnreadWorkshopMessages} uleste
          </span>
        )}
        {workshopServicesWithUnread > 0 && bookingsWithUnread > 0 && <span> · </span>}
        {bookingsWithUnread > 0 && (
          <span>
            Bookinger: {bookingsWithUnread} med {totalUnreadBookingMessages} uleste
          </span>
        )}
      </div>
    </div>

<button
  onClick={() => setUnreadModalOpen(true)}
  className="inline-flex min-h-[2.5rem] shrink-0 items-center rounded-xl bg-red-700 px-3 py-1.5 font-semibold text-white hover:bg-red-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
>
  Vis alle
</button>
  </div>
)}

      <BookingTableSection
        title={showAllBookings ? "Bookinger med leveringsdato innen 6 dager" : "Bookinger som skal leveres i dag eller er forsinket"}
        rows={filteredBookingRows}
        navigate={navigate}
        showAll={showAllBookings}
        onToggleShowAll={() => setShowAllBookings(v => !v)}
        futurePendingCount={futurePendingCount}
      />

      <TableSection
        title="Manglende ferdigdato"
        rows={grouped.withoutDate}
        navigate={navigate}
      />

      <TableSection
        title="Ferdigdato innen 7 dager"
        rows={grouped.next7Days}
        navigate={navigate}
      />

<LaterCombinedTableSection
  title="Ferdigdato senere enn 7 dager"
  rows={laterCombinedRows}
  navigate={navigate}
/>

{unreadModalOpen && (
  <div
    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    onClick={() => setUnreadModalOpen(false)}
  >
    <div
      className="bg-white w-full max-w-2xl rounded-2xl shadow-lg overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <div>
          <h3 className="text-lg font-semibold">Uleste kundemeldinger</h3>
          <p className="text-sm text-slate-600 mt-1">
            {unreadItems.length} ordre med uleste meldinger
          </p>
        </div>

        <button
          onClick={() => setUnreadModalOpen(false)}
          className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
        >
          Lukk
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        {unreadItems.length === 0 ? (
          <div className="p-6 text-sm text-slate-600">
            Ingen uleste meldinger.
          </div>
        ) : (
          <div className="divide-y">
            {unreadItems.map((item) => (
              <button
                key={`${item.source}-${item.id}`}
                type="button"
                title={
                  item.latestMessageText
                    ? `Siste melding:\n${item.latestMessageText}${
                        item.latestMessageAt
                          ? `\n\n${new Date(item.latestMessageAt).toLocaleString("no-NO")}`
                          : ""
                      }`
                    : "Ingen meldingsforhåndsvisning"
                }
                onClick={() => {
                  setUnreadModalOpen(false);
                  navigate(
                    item.source === "workshop"
                      ? `/workshop/${item.id}`
                      : `/booking/${item.id}`
                  );
                }}
                className="w-full text-left px-5 py-4 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-slate-900">
                      {item.title}
                    </div>

                    <div className="text-sm text-slate-600 mt-1">
                      {item.subtitle}
                    </div>

                    {item.latestMessageText ? (
                      <div className="text-xs text-slate-500 mt-1 truncate">
                        {item.latestMessageText}
                      </div>
                    ) : null}
                  </div>

                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 shrink-0">
                    💬 {item.unreadCount}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
)}

    {snapOpen && (
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => setSnapOpen(false)}
      >
        <div
          className="bg-white w-full max-w-sm rounded-2xl shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h4 className="text-base font-semibold">Ny snap-service</h4>
            <button type="button" onClick={() => setSnapOpen(false)} className="text-slate-600 hover:text-black">Lukk</button>
          </div>
          <div className="px-4 py-3 space-y-3">
            <p className="text-xs text-slate-600">
              Fortløpende service uten kunderegistrering. Mekaniker fyller inn detaljer i WorkshopDetails.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Beskrivelse (valgfritt)</label>
              <input
                type="text"
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="F.eks. Dekkskift, Bremsejustering…"
                value={snapDesc}
                onChange={(e) => setSnapDesc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !snapBusy && createSnap()}
                autoFocus
              />
            </div>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" onClick={() => setSnapOpen(false)} className="px-3 py-1.5 rounded-lg border hover:bg-slate-50 text-sm">
              Avbryt
            </button>
            <button
              type="button"
              onClick={createSnap}
              disabled={snapBusy}
              className={`px-3 py-1.5 rounded-lg text-white text-sm font-medium ${snapBusy ? "bg-slate-400 cursor-not-allowed" : "bg-emerald-700 hover:bg-emerald-800"}`}
            >
              {snapBusy ? "Oppretter…" : "Opprett"}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}